import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { supabase } from "./supabaseClient"

const V = "#7C3AED"
const V_SOFT = "#F3F0FF"
const WHITE = "#FFFFFF"
const OFF = "#FAFAFA"
const TEXT = "#09090B"
const MUTED = "#71717A"
const BORDER = "#E4E4E7"

const FONT_URL = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Manrope:wght@300;400;500;600&display=swap"
const H = "'Plus Jakarta Sans', system-ui, sans-serif"
const B = "'Manrope', system-ui, sans-serif"

const T = {
  es: {
    title: "Recupera tu contraseña",
    subtitle: "Escribe tu correo y te enviamos un enlace para crear una nueva.",
    emailLabel: "Correo electrónico",
    emailPlaceholder: "tu@correo.com",
    button: "Enviar enlace",
    loading: "Enviando...",
    okTitle: "Revisa tu correo",
    okText: "Si esa dirección tiene una cuenta en KORIVA Studio, te enviamos un enlace para crear tu nueva contraseña. El enlace vence en una hora.",
    errorEmail: "Escribe un correo válido.",
    errorGeneric: "Algo salió mal. Intenta de nuevo.",
    backTo: "¿Ya la recordaste?",
    backLink: "Iniciar sesión"
  },
  en: {
    title: "Reset your password",
    subtitle: "Enter your email and we'll send you a link to create a new one.",
    emailLabel: "Email",
    emailPlaceholder: "you@email.com",
    button: "Send link",
    loading: "Sending...",
    okTitle: "Check your email",
    okText: "If that address has a KORIVA Studio account, we've sent a link to create your new password. The link expires in one hour.",
    errorEmail: "Enter a valid email.",
    errorGeneric: "Something went wrong. Try again.",
    backTo: "Remembered it?",
    backLink: "Sign in"
  }
}

export default function KorivaRecuperar() {
  const navigate = useNavigate()
  const [lang, setLang] = useState("es")
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [enviado, setEnviado] = useState(false)

  const t = T[lang]

  async function handleEnviar() {
    if (!email.trim() || !email.includes("@")) {
      setError(t.errorEmail)
      return
    }
    setLoading(true)
    setError("")
    try {
      await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: window.location.origin + "/nueva-clave"
      })
      setEnviado(true)
    } catch (err) {
      setError(t.errorGeneric)
    } finally {
      setLoading(false)
    }
  }

  function onKeyDown(e) {
    if (e.key === "Enter") handleEnviar()
  }

  const s = {
    page: {
      minHeight: "100vh",
      background: OFF,
      fontFamily: B,
      color: TEXT,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "24px"
    },
    card: {
      background: WHITE,
      borderRadius: "20px",
      border: `1px solid ${BORDER}`,
      boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
      padding: "40px",
      width: "100%",
      maxWidth: "420px"
    },
    logo: {
      fontFamily: H,
      fontWeight: 800,
      fontSize: "24px",
      color: V,
      letterSpacing: "-0.02em",
      marginBottom: "8px"
    },
    title: {
      fontFamily: H,
      fontWeight: 700,
      fontSize: "26px",
      margin: "0 0 6px 0",
      letterSpacing: "-0.02em"
    },
    subtitle: {
      fontSize: "15px",
      color: MUTED,
      margin: "0 0 28px 0",
      lineHeight: 1.6
    },
    label: {
      display: "block",
      fontFamily: H,
      fontWeight: 600,
      fontSize: "14px",
      marginBottom: "8px"
    },
    input: {
      width: "100%",
      boxSizing: "border-box",
      padding: "13px 16px",
      borderRadius: "12px",
      border: `1px solid ${BORDER}`,
      fontSize: "15px",
      fontFamily: B,
      outline: "none",
      marginBottom: "18px",
      background: WHITE
    },
    button: {
      width: "100%",
      padding: "14px",
      borderRadius: "12px",
      border: "none",
      background: V,
      color: WHITE,
      fontFamily: H,
      fontWeight: 700,
      fontSize: "16px",
      cursor: loading ? "default" : "pointer",
      opacity: loading ? 0.7 : 1,
      marginTop: "6px"
    },
    error: {
      background: "#FEF2F2",
      color: "#DC2626",
      borderRadius: "10px",
      padding: "12px 14px",
      fontSize: "14px",
      marginBottom: "18px",
      border: "1px solid #FECACA"
    },
    okIcon: {
      width: 56,
      height: 56,
      background: V_SOFT,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: "20px",
      fontSize: 22,
      color: V
    },
    footer: {
      textAlign: "center",
      fontSize: "14px",
      color: MUTED,
      marginTop: "22px"
    },
    link: {
      color: V,
      fontWeight: 600,
      cursor: "pointer",
      textDecoration: "none"
    },
    langSwitch: {
      display: "flex",
      gap: "6px",
      justifyContent: "flex-end",
      marginBottom: "20px"
    },
    langBtn: (active) => ({
      padding: "5px 12px",
      borderRadius: "8px",
      border: `1px solid ${active ? V : BORDER}`,
      background: active ? V_SOFT : WHITE,
      color: active ? V : MUTED,
      fontFamily: H,
      fontWeight: 600,
      fontSize: "13px",
      cursor: "pointer"
    })
  }

  return (
    <div style={s.page}>
      <link rel="stylesheet" href={FONT_URL} />
      <div style={s.card}>
        <div style={s.langSwitch}>
          <button style={s.langBtn(lang === "es")} onClick={() => setLang("es")}>ES</button>
          <button style={s.langBtn(lang === "en")} onClick={() => setLang("en")}>EN</button>
        </div>
        <div style={s.logo}>KORIVA Studio</div>

        {enviado ? (
          <>
            <div style={s.okIcon}>✓</div>
            <h1 style={s.title}>{t.okTitle}</h1>
            <p style={s.subtitle}>{t.okText}</p>
            <div style={s.footer}>
              <span style={s.link} onClick={() => navigate("/login")}>{t.backLink}</span>
            </div>
          </>
        ) : (
          <>
            <h1 style={s.title}>{t.title}</h1>
            <p style={s.subtitle}>{t.subtitle}</p>

            {error && <div style={s.error}>{error}</div>}

            <label style={s.label}>{t.emailLabel}</label>
            <input
              style={s.input}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={t.emailPlaceholder}
              autoComplete="email"
            />

            <button style={s.button} onClick={handleEnviar} disabled={loading}>
              {loading ? t.loading : t.button}
            </button>

            <div style={s.footer}>
              {t.backTo}{" "}
              <span style={s.link} onClick={() => navigate("/login")}>{t.backLink}</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
